import { useState, useEffect } from 'react'
import Card from '../components/Card'
import Button from '../components/Button'

export default function ColorConverter() {
  const [inputFormat, setInputFormat] = useState('hex')
  const [inputValue, setInputValue] = useState('#3b82f6')
  const [convertedValues, setConvertedValues] = useState({
    hex: '#3b82f6',
    rgb: 'rgb(59, 130, 246)',
    hsl: 'hsl(217, 91%, 60%)',
  })

  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
    return result
      ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16),
        }
      : null
  }

  const rgbToHsl = (r, g, b) => {
    r /= 255
    g /= 255
    b /= 255
    const max = Math.max(r, g, b)
    const min = Math.min(r, g, b)
    let h, s, l = (max + min) / 2

    if (max === min) {
      h = s = 0
    } else {
      const d = max - min
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
      switch (max) {
        case r:
          h = ((g - b) / d + (g < b ? 6 : 0)) / 6
          break
        case g:
          h = ((b - r) / d + 2) / 6
          break
        case b:
          h = ((r - g) / d + 4) / 6
          break
        default:
          h = 0
      }
    }

    return {
      h: Math.round(h * 360),
      s: Math.round(s * 100),
      l: Math.round(l * 100),
    }
  }

  const parseRgb = (rgb) => {
    const match = rgb.match(/\d+/g)
    return match ? { r: parseInt(match[0]), g: parseInt(match[1]), b: parseInt(match[2]) } : null
  }

  const convert = () => {
    let rgb = null

    if (inputFormat === 'hex') {
      rgb = hexToRgb(inputValue)
    } else if (inputFormat === 'rgb') {
      rgb = parseRgb(inputValue)
    }

    if (rgb) {
      const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b)
      const hex = `#${((1 << 24) + (rgb.r << 16) + (rgb.g << 8) + rgb.b).toString(16).slice(1)}`

      setConvertedValues({
        hex: hex.toUpperCase(),
        rgb: `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`,
        hsl: `hsl(${hsl.h}, ${hsl.s}%, ${hsl.l}%)`,
      })
    }
  }

  const handleCopy = (value) => {
    navigator.clipboard.writeText(value)
    alert('Copied to clipboard!')
  }

  // Auto-convert on input change
  useEffect(() => {
    if (inputValue) {
      convert()
    }
  }, [inputValue, inputFormat])

  return (
    <div className="space-y-6">
      <Card>
        <Card.Body>
          <div className="mb-6">
            <label className="label">Input Format:</label>
            <select
              value={inputFormat}
              onChange={(e) => setInputFormat(e.target.value)}
              className="input w-full mb-2"
            >
              <option value="hex">HEX</option>
              <option value="rgb">RGB</option>
            </select>
            <input
              type={inputFormat === 'hex' ? 'color' : 'text'}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              className={inputFormat === 'hex' ? 'w-full h-32 rounded-lg cursor-pointer' : 'input w-full'}
              placeholder={inputFormat === 'rgb' ? 'rgb(59, 130, 246)' : ''}
            />
            <Button onClick={convert} variant="primary" className="w-full mt-4">
              Convert
            </Button>
          </div>

          <div className="space-y-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex justify-between items-center">
                <div>
                  <label className="text-sm font-semibold text-gray-700">HEX:</label>
                  <div className="text-xl font-mono text-primary-600">{convertedValues.hex}</div>
                </div>
                <Button onClick={() => handleCopy(convertedValues.hex)} variant="ghost" size="sm">
                  Copy
                </Button>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex justify-between items-center">
                <div>
                  <label className="text-sm font-semibold text-gray-700">RGB:</label>
                  <div className="text-xl font-mono text-primary-600">{convertedValues.rgb}</div>
                </div>
                <Button onClick={() => handleCopy(convertedValues.rgb)} variant="ghost" size="sm">
                  Copy
                </Button>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex justify-between items-center">
                <div>
                  <label className="text-sm font-semibold text-gray-700">HSL:</label>
                  <div className="text-xl font-mono text-primary-600">{convertedValues.hsl}</div>
                </div>
                <Button onClick={() => handleCopy(convertedValues.hsl)} variant="ghost" size="sm">
                  Copy
                </Button>
              </div>
            </div>

            <div
              className="w-full h-32 rounded-lg border-2 border-gray-300"
              style={{ backgroundColor: convertedValues.hex }}
            ></div>
          </div>
        </Card.Body>
      </Card>
    </div>
  )
}

